
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { 
  LayoutDashboard, 
  Library, 
  History, 
  LogOut, 
  Microscope,
  LogIn,
  Layers,
  PlusCircle,
  WifiOff,
  CloudOff
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/Button';
import { motion, AnimatePresence } from 'framer-motion';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useAppStore(state => state.logout);
  const user = useAppStore(state => state.user);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const updateStatus = () => {
      const online = navigator.onLine;
      setIsOnline(online);
      // Disable pull-to-refresh ONLY when offline
      if (!online) {
        document.documentElement.classList.add('is-offline');
      } else {
        document.documentElement.classList.remove('is-offline');
      }
    };

    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);
    
    // Initial check
    updateStatus();

    return () => {
      window.removeEventListener('online', updateStatus);
      window.removeEventListener('offline', updateStatus);
    };
  }, []);

  const navigation = [
    { name: 'Home', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Library', href: '/library', icon: Library },
    { name: 'Flashcards', href: '/flashcards', icon: Layers },
    { name: 'Practice', href: '/practice', icon: Microscope },
    { name: 'History', href: '/history', icon: History },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Simplified Top Navigation */}
      <header className="sticky top-0 z-[70] bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center gap-8">
              <Link to={user ? "/dashboard" : "/"} className="flex items-center group">
                <div className="p-1.5 bg-primary-500 rounded-lg group-hover:bg-primary-600 transition-colors">
                  <Microscope className="h-5 w-5 text-white" />
                </div>
                <span className="ml-2.5 text-lg font-semibold text-slate-900 tracking-tight">HistologyPro</span>
              </Link>

              {/* Desktop Nav */}
              <nav className="hidden md:flex items-center space-x-1">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href));
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        "px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                        isActive 
                          ? "bg-slate-100 text-slate-900" 
                          : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                      )}
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>

            <div className="flex items-center gap-4">
              <Link to="/upload" className="hidden sm:block">
                <Button size="sm" variant="primary" className="rounded-full shadow-none font-semibold">
                  <PlusCircle className="h-4 w-4 mr-2" /> Upload
                </Button>
              </Link>
              
              {user ? (
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <button onClick={handleLogout} className="text-slate-400 hover:text-red-500 transition-colors">
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <Link to="/signup">
                  <Button size="sm" variant="ghost" className="text-xs font-bold">Sign In</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Offline Status Banner */}
      <AnimatePresence>
        {!isOnline && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-slate-800 text-white overflow-hidden"
          >
            <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-3">
              <WifiOff className="h-4 w-4 text-slate-400" />
              <span className="text-[10px] font-black uppercase tracking-[0.2em]">Offline Mode: Browsing Cached Library</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Nav Bar (Bottom) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50 flex items-center justify-around h-16 px-4">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href));
          return (
            <Link key={item.name} to={item.href} className={cn(
              "flex flex-col items-center gap-1",
              isActive ? "text-primary-500" : "text-slate-400"
            )}>
              <item.icon className="h-5 w-5" />
              <span className="text-[10px] font-medium">{item.name}</span>
            </Link>
          );
        })}
      </nav>

      {/* Content */}
      <main className="flex-1 pb-24 md:pb-12 pt-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {!user && isOnline && (
            <div className="mb-6 bg-amber-50 border border-amber-100 p-3 rounded-xl flex items-center justify-between">
              <p className="text-xs text-amber-800 font-medium">Guest Mode: Save progress by creating an account.</p>
              <Link to="/signup"><span className="text-xs font-bold text-amber-900 underline">Join Now</span></Link>
            </div>
          )}
          {!isOnline && (
            <div className="mb-6 bg-slate-100 border border-slate-200 p-3 rounded-xl flex items-center gap-3">
              <CloudOff className="h-4 w-4 text-slate-400" />
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Syncing disabled until connection restored.</p>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
};
